/**
 * Localization Constants
 * Table names, locale codes, and content type categorization for the localization system
 */

/**
 * Database Table Names
 * Single source of truth for localization-related table names
 */
export const LOCALIZATION_TABLES = {
  /** UI strings (keys, values, translations) - quality managed via needs_review + issues[] */
  USER_INTERFACE: 'localization_user_interface',
  /** Translation glossary for consistent terminology */
  GLOSSARY: 'localization_glossary',
  /** Translation memory for reuse */
  MEMORY: 'localization_memory',
  /** Archived/deleted translations */
  ARCHIVE: 'localization_archive',
  /** Source file scan results */
  FILE_SCANS: 'localization_file_scans',
} as const;

/**
 * Source Locale Constants
 * The base language for system-controlled content
 */
export const SOURCE_LOCALE = 'en';

/**
 * Default Regional Locale
 * Used for user-facing i18n when no specific region is detected
 */
export const DEFAULT_REGIONAL_LOCALE = 'en-US';

/**
 * Bootstrap Locale - Used for i18n initialization before database loads
 * This is the base language code for source content
 * 
 * WHY 'en' not 'en-US':
 * - i18next initializes before React Query can fetch from database
 * - 'en' represents the source language, minimal bootstrap config
 * - Regional variants are loaded dynamically from system_locale_configs
 * 
 * ESLint rule `no-hardcoded-locale-arrays` enforces this as ultimate fallback
 */
export const BOOTSTRAP_LOCALE = 'en';

/**
 * Bootstrap Ultimate Fallback
 * MUST be 'en' (source language), not 'en-US' (regional variant)
 * ESLint rule enforces this - any fallback chain ending with 'en-US' is an error
 */
export const BOOTSTRAP_ULTIMATE_FALLBACK = BOOTSTRAP_LOCALE;

/**
 * Bootstrap RTL Languages - For i18n init before database loads
 * Runtime RTL detection uses is_rtl column from system_locale_configs
 * 
 * These are base language codes - the database has is_rtl=true for:
 * ar, ar-AE, ar-EG, ar-SA, he, he-IL, fa, fa-IR
 */
export const BOOTSTRAP_RTL_LANGUAGES = ['ar', 'he', 'fa'] as const;

/**
 * Check if a locale is RTL (bootstrap version - base language extraction)
 * Use database is_rtl column at runtime for accurate detection
 * @param locale - Full locale code (e.g., 'ar-SA') or base code ('ar')
 */
export function isRtlLocaleBootstrap(locale: string): boolean {
  const base = locale.split('-')[0];
  return BOOTSTRAP_RTL_LANGUAGES.includes(base as typeof BOOTSTRAP_RTL_LANGUAGES[number]);
}

/**
 * Get base language from a locale
 * @param locale - Full locale code (e.g., 'es-AR') or base code ('es')
 */
export function getBaseLanguage(locale: string): string {
  return locale.split('-')[0];
}

/**
 * System-Controlled Content Types
 * These entities are platform-defined and always originate in English International (EN).
 * They are AI-translated from the base EN source to all enabled locales.
 * 
 * Translation Flow:
 * 1. Content created in base English (source tables)
 * 2. AI cascade translates to all 60 enabled locales
 * 3. localization_metadata stores all translations (including en-US, en-GB regional adaptations)
 */
export const SYSTEM_CONTENT_TYPES = [
  'location',        // locations table → base EN → 60 locales
  'page',            // content_institutional_pages → base EN → 60 locales
  'route',           // System-defined routes → base EN → 60 locales
  'faq',             // help_center_faqs → base EN → 60 locales
  'email_template',  // email_templates → base EN → 60 locales (non-indexed)
  'practice_area',      // users_practice_areas → base EN → 36 locales (non-indexed)
  'membership_plan',   // users_membership_plans → base EN → 36 locales (non-indexed)
  'content_type',    // content_types → base EN → 36 locales (non-indexed)
] as const;

/**
 * User/Location-Generated Content Types
 * These entities can originate from any location and any language.
 * The source_locale field tracks the original language.
 * 
 * Translation Flow:
 * 1. Content created in author's native language (stored with source_locale)
 * 2. AI cascade translates to all OTHER locales (including English if source isn't English)
 * 3. localization_metadata stores translations, source_locale tracks origin
 */
export const USER_CONTENT_TYPES = [
  'publication',       // content_submissions → author's locale → other locales
  'profile',       // users + user_bios → user's locale → other locales
  'comment',       // content_comments → commenter's locale → other locales
  'topic',         // topics → location-specific → other locales
] as const;

/**
 * All supported content types for localization_metadata
 */
export const ALL_CONTENT_TYPES = [...SYSTEM_CONTENT_TYPES, ...USER_CONTENT_TYPES] as const;

/**
 * Indexable Entity Types
 * These entities require full SEO metadata and are included in sitemaps.
 * They contribute to SEO Coverage metrics.
 * 
 * DERIVED from entitySourceConfig.ts - Single Source of Truth
 * Import dynamically to avoid circular dependency at module load time
 */
export const getIndexableEntityTypesFromConfig = (): LocalizationContentType[] => {
  // Lazy import to avoid circular dependency
  const { getIndexableEntityTypes } = require('@/config/entitySourceConfig');
  return getIndexableEntityTypes();
};

/**
 * Non-Indexable Entity Types
 * These entities are part of parent pages and only need translation fields.
 * They have no_index=true and contribute to Translation Coverage metrics.
 * 
 * DERIVED from entitySourceConfig.ts - Single Source of Truth
 */
export const getNonIndexableEntityTypesFromConfig = (): LocalizationContentType[] => {
  // Lazy import to avoid circular dependency
  const { getNonIndexableEntityTypes } = require('@/config/entitySourceConfig');
  return getNonIndexableEntityTypes();
};

// Static arrays for type inference (must match entitySourceConfig values)
// These are kept for backward compatibility with code that expects static arrays
export const INDEXABLE_ENTITY_TYPES = [
  'location',  // Country/region pages
  'topic',     // Tax topic pages  
  'publication',   // Published publications
  'page',      // Institutional pages
  'route',     // System routes
  'profile',   // Expert profiles
] as const;

export const NON_INDEXABLE_ENTITY_TYPES = [
  'faq',            // Part of parent page, pre-generated translations
  'email_template', // Transactional emails, pre-generated translations
  'comment',        // Part of publication, on-demand translations
  'practice_area',     // Part of profile/filter UI, translation-only
  'membership_plan',  // International membership plans, manual translation-only
] as const;

// Type definitions
export type SystemContentType = typeof SYSTEM_CONTENT_TYPES[number];
export type UserContentType = typeof USER_CONTENT_TYPES[number];
export type LocalizationContentType = typeof ALL_CONTENT_TYPES[number];
export type IndexableEntityType = typeof INDEXABLE_ENTITY_TYPES[number];
export type NonIndexableEntityType = typeof NON_INDEXABLE_ENTITY_TYPES[number];

/**
 * Check if an entity type is indexable (requires full SEO metadata)
 * Uses entitySourceConfig as source of truth
 */
export const isIndexableEntity = (type: string): type is IndexableEntityType => {
  // Use entitySourceConfig as the source of truth
  try {
    const { isEntityIndexable } = require('@/config/entitySourceConfig');
    return isEntityIndexable(type as LocalizationContentType);
  } catch {
    // Fallback to static array if import fails
    return INDEXABLE_ENTITY_TYPES.includes(type as IndexableEntityType);
  }
};

/**
 * Check if an entity type is non-indexable (translation only)
 * Uses entitySourceConfig as source of truth
 */
export const isNonIndexableEntity = (type: string): type is NonIndexableEntityType => {
  return !isIndexableEntity(type) && ALL_CONTENT_TYPES.includes(type as LocalizationContentType);
};

/**
 * Check if a content type is system-controlled (EN International source)
 */
export const isSystemContent = (type: string): type is SystemContentType => {
  return SYSTEM_CONTENT_TYPES.includes(type as SystemContentType);
};

/**
 * Check if a content type is user-generated (variable source language)
 */
export const isUserContent = (type: string): type is UserContentType => {
  return USER_CONTENT_TYPES.includes(type as UserContentType);
};

/**
 * Get the source locale logic for a content type
 * - System content: Always 'en' (base English from source tables)
 * - User content: Variable (check source_locale field on the entity)
 */
export const getSourceLocaleStrategy = (type: string): 'fixed_en' | 'variable' => {
  return isSystemContent(type) ? 'fixed_en' : 'variable';
};

/**
 * Content type metadata for cascade translation logic
 */
export const CONTENT_TYPE_CONFIG: Record<LocalizationContentType, {
  sourceStrategy: 'fixed_en' | 'variable';
  sourceTable: string;
  translateToEnglish: boolean;
  description: string;
}> = {
  // System content - EN International source
  location: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'locations',
    translateToEnglish: false, // Source IS English
    description: 'Platform locations (countries, regions)',
  },
  page: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'content_institutional_pages',
    translateToEnglish: false,
    description: 'Institutional pages (About, Terms, etc.)',
  },
  route: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'system_routes',
    translateToEnglish: false,
    description: 'System route slugs for navigation',
  },
  faq: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'help_center_faqs',
    translateToEnglish: false,
    description: 'Help center FAQ entries',
  },
  email_template: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'email_templates',
    translateToEnglish: false,
    description: 'Transactional email template content',
  },
  practice_area: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'users_practice_areas',
    translateToEnglish: false,
    description: 'Expert practice areas / service types',
  },
  membership_plan: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'users_membership_plans',
    translateToEnglish: false,
    description: 'International membership plans (manual translation)',
  },
  content_type: {
    sourceStrategy: 'fixed_en',
    sourceTable: 'content_types',
    translateToEnglish: false,
    description: 'Content submission type labels',
  },
  
  // User content - variable source language
  publication: {
    sourceStrategy: 'variable',
    sourceTable: 'content_submissions',
    translateToEnglish: true, // May need EN translation if source is non-EN
    description: 'User-submitted publications and content',
  },
  profile: {
    sourceStrategy: 'variable',
    sourceTable: 'users',
    translateToEnglish: true,
    description: 'Expert profiles and bios',
  },
  comment: {
    sourceStrategy: 'variable',
    sourceTable: 'content_comments',
    translateToEnglish: true,
    description: 'Publication comments from users',
  },
  topic: {
    sourceStrategy: 'variable',
    sourceTable: 'topics',
    translateToEnglish: true,
    description: 'Tax topics (may be location-specific)',
  },
};
